import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type { Advogado } from '@/types/advogado';

interface AdvogadoFilters {
  search?: string;
  especialidade?: string;
  ativo?: boolean;
  page?: number;
  limit?: number;
}

interface AdvogadoCreateDto {
  nome: string;
  email: string;
  oabNumero: string;
  oabEstado: string;
  especialidades?: string[];
  ativo?: boolean;
}

type AdvogadoUpdateDto = Partial<AdvogadoCreateDto>;

export const advogadoService = {
  listar: async (filters?: AdvogadoFilters): Promise<{ data: Advogado[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.especialidade) params.append('especialidade', filters.especialidade);
    if (filters?.ativo !== undefined) params.append('ativo', String(filters.ativo));
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/advogados${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao listar advogados' }));
      throw new Error(errorData.error || 'Erro ao listar advogados');
    }

    const result = await response.json();
    const rawData = result.data?.data || result.data || [];
    const data: Advogado[] = Array.isArray(rawData) ? rawData : [];
    const total: number = result.data?.pagination?.total || data.length || 0;
    return { data, total };
  },

  buscarPorId: async (id: string): Promise<Advogado> => {
    const response = await apiGet(`/advogados/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Advogado não encontrado' }));
      throw new Error(errorData.error || 'Advogado não encontrado');
    }
    const result = await response.json();
    return result.data as Advogado;
  },

  criar: async (payload: AdvogadoCreateDto): Promise<Advogado> => {
    const response = await apiPost('/advogados', payload);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao criar advogado' }));
      throw new Error(errorData.error || 'Erro ao criar advogado');
    }
    const result = await response.json();
    return result.data as Advogado;
  },

  atualizar: async (id: string, payload: AdvogadoUpdateDto): Promise<Advogado> => {
    const response = await apiPut(`/advogados/${id}`, payload);
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao atualizar advogado' }));
      throw new Error(errorData.error || 'Erro ao atualizar advogado');
    }
    const result = await response.json();
    return result.data as Advogado;
  },

  remover: async (id: string): Promise<void> => {
    const response = await apiDelete(`/advogados/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao remover advogado' }));
      throw new Error(errorData.error || 'Erro ao remover advogado');
    }
  },
};
